import type { PipelineStage } from "@/lib/pipelineStage";
import type { CustomerInsights } from "@/lib/customerInsights";

export interface SampleCustomer {
  customer_id: string;
  short_id: string;
  name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  created_at: string;
  total_order_value: number;
  recent_order_value: number;
  last_activity_at: string | null;
  preferred_channel: string | null;
  total_bookings: number;
  graph_stage: PipelineStage;
  insights: CustomerInsights;
  is_sample: true;
}

export const DEMO_SAMPLE_CUSTOMER_ID = "sample-customer-1";

export function isDemoSampleCustomer(customerId: string | null | undefined) {
  return customerId === DEMO_SAMPLE_CUSTOMER_ID;
}

const SAMPLE_BLUEPRINTS: Array<{
  name: string;
  email: string;
  phone: string | null;
  channel: string | null;
  bookings: number;
  total: number;
  recent: number;
  joinedDaysAgo: number;
  activeDaysAgo: number | null;
  stage: PipelineStage;
  insights: CustomerInsights;
}> = [
  { name: "Avery Tan", email: "avery.tan@example.com", phone: "+65 6000 0001", channel: "whatsapp", bookings: 8, total: 840, recent: 280, joinedDaysAgo: 210, activeDaysAgo: 4, stage: "vip", insights: { interest: "Monthly 60-minute appointment", bestTime: "Weekday evenings", sentiment: "positive", nextStep: "Hold the usual Thursday evening slot" } },
  { name: "Jordan Lim", email: "jordan.lim@example.com", phone: "+65 6000 0002", channel: "email", bookings: 4, total: 320, recent: 80, joinedDaysAgo: 130, activeDaysAgo: 12, stage: "active", insights: { interest: "Package of five sessions", bestTime: "Weekend mornings", sentiment: "positive", nextStep: "Ask for a review" } },
  { name: "Sofia Lee", email: "sofia.lee@example.com", phone: null, channel: "instagram", bookings: 3, total: 240, recent: 60, joinedDaysAgo: 95, activeDaysAgo: 21, stage: "active", insights: { interest: "New seasonal offer", bestTime: "Weekday afternoons", sentiment: "neutral", nextStep: "Share the price list" } },
  { name: "Noah Wong", email: "noah.wong@example.com", phone: "+65 6000 0004", channel: "whatsapp", bookings: 2, total: 160, recent: 0, joinedDaysAgo: 100, activeDaysAgo: 42, stage: "at-risk", insights: { interest: "Rescheduling a missed visit", bestTime: "Weekday evenings", sentiment: "frustrated", nextStep: "Call back about the reschedule" } },
  { name: "Maya Chen", email: "maya.chen@example.com", phone: null, channel: "email", bookings: 2, total: 140, recent: 0, joinedDaysAgo: 150, activeDaysAgo: 76, stage: "dormant", insights: { interest: "Gift vouchers", bestTime: "Weekend afternoons", sentiment: "neutral", nextStep: "Send a we-miss-you note" } },
  { name: "Ethan Ng", email: "ethan.ng@example.com", phone: "+65 6000 0006", channel: "telegram", bookings: 1, total: 75, recent: 0, joinedDaysAgo: 240, activeDaysAgo: 205, stage: "lapsed", insights: { interest: "Intro session", bestTime: "Weekday mornings", sentiment: null, nextStep: "Invite back with an intro slot" } },
  { name: "Chloe Goh", email: "chloe.goh@example.com", phone: "+65 6000 0007", channel: "whatsapp", bookings: 1, total: 95, recent: 95, joinedDaysAgo: 28, activeDaysAgo: 8, stage: "active", insights: { interest: "Second appointment", bestTime: "Weekend afternoons", sentiment: "positive", nextStep: "Book the follow-up" } },
  { name: "Lucas Ong", email: "lucas.ong@example.com", phone: null, channel: null, bookings: 0, total: 0, recent: 0, joinedDaysAgo: 14, activeDaysAgo: null, stage: "new", insights: { interest: null, bestTime: null, sentiment: null, nextStep: null } },
  { name: "Amelia Koh", email: "amelia.koh@example.com", phone: "+65 6000 0009", channel: "email", bookings: 1, total: 110, recent: 0, joinedDaysAgo: 180, activeDaysAgo: 58, stage: "at-risk", insights: { interest: "Lower-priced option", bestTime: "Weekday mornings", sentiment: "neutral", nextStep: "Share the fees" } },
  { name: "Ryan Ho", email: "ryan.ho@example.com", phone: null, channel: null, bookings: 0, total: 0, recent: 0, joinedDaysAgo: 6, activeDaysAgo: null, stage: "new", insights: { interest: null, bestTime: null, sentiment: null, nextStep: null } },
];

const daysBefore = (now: Date, days: number) =>
  new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();

export function createSampleCustomers(now = new Date()): SampleCustomer[] {
  return SAMPLE_BLUEPRINTS.map((sample, index) => ({
    customer_id: `sample-customer-${index + 1}`,
    short_id: `SAMPLE-${String(index + 1).padStart(2, "0")}`,
    name: `${sample.name} (sample)`,
    email: sample.email,
    phone: sample.phone,
    notes: "Sample customer — replaced automatically when real customer data is added.",
    created_at: daysBefore(now, sample.joinedDaysAgo),
    total_order_value: sample.total,
    recent_order_value: sample.recent,
    last_activity_at: sample.activeDaysAgo === null ? null : daysBefore(now, sample.activeDaysAgo),
    preferred_channel: sample.channel,
    total_bookings: sample.bookings,
    graph_stage: sample.stage,
    insights: sample.insights,
    is_sample: true,
  }));
}

export function shouldShowSampleCustomers({
  realCustomerCount,
  retiredAt,
  stateLoaded,
  customerQueryFailed,
}: {
  realCustomerCount: number;
  retiredAt: string | null | undefined;
  stateLoaded: boolean;
  customerQueryFailed: boolean;
}) {
  return stateLoaded && !customerQueryFailed && realCustomerCount === 0 && retiredAt === null;
}
